// ════════════════════════════════════════════════════════════════════
// TERROR — THE SCRIPT FORMAT
// ────────────────────────────────────────────────────────────────────
// One function, no React, no imports. It lives apart from
// StoryCutscene.js for exactly one reason: a parser that decides how
// every line in the game is spoken must be testable without a DOM, and
// the moment it sat inside a .js file full of JSX it was not.
// storyScript.selftest.mjs runs it against every authored dialogue line
// in the codebase on every pass.
//
//   plain line          → the scene's speaker
//   "Name: line"        → THAT person, for this line only
//   "[line]"            → the player
//   "*line*"            → a stage direction
//   "*aside* line"      → handled downstream by <Spoken>, not here
// ════════════════════════════════════════════════════════════════════

// ── THE THIRD VOICE ─────────────────────────────────────────────────
// A scene has one `speaker`, which was right until the first scene with
// three people in it — the confrontation where Lyraei & Lestre, V'ael
// and J'ynxi all talk. The options were a speaker field per line
// (caP'n stops being able to paste scripts), one conversation split
// into four chained cutscenes (the name-plate flickers), or a prefix.
//
// The prefix wins because it is what he already types: "SHV: bold of
// you two to enter...". It is deliberately NARROW so it can never eat a
// line that merely contains a colon: the name must LEAD the line, be
// 1–20 characters, and contain nothing but letters, spaces, apostrophes,
// hyphens and ampersands. "◆ The gate holds: as it always has." begins
// with ◆ and is therefore safe, as is any line with punctuation, a
// digit or a symbol before its colon.
//
// If the selftest ever fails, the RULE is too wide. Narrow the rule;
// do not edit the line that tripped it. It has already been narrowed
// once, by that test, and this is the narrowing:
//
//   ⚠ "It went like this: badly."
//
// Every character in "It went like this" is legal, it is seventeen
// characters, it leads the line, and it is not a name. Shape alone
// cannot tell the difference — so a second, semantic gate: A NAME IS
// TITLE CASE. At most four words, every one of them capitalised (or a
// bare "&"). That admits V'ael, Pipe-Lich, The Commanding Champion and
// Lyraei & Lestre, and refuses any ordinary clause, because ordinary
// clauses contain lowercase words and names do not.
export const NAMED = /^([A-Za-z][A-Za-z'’\-& ]{0,19}):[ \t]+(\S.*)$/;

export const isName = (s) => {
  const words = String(s).trim().split(/\s+/).filter(Boolean);
  if (!words.length || words.length > 4) return false;
  return words.every((w) => w === "&" || /^[A-Z]/.test(w));
};

export function readLine(raw) {
  const t = String(raw ?? "").trim();
  if (!t) return { kind: "beat", text: "" };
  if (t.startsWith("[") && t.endsWith("]")) return { kind: "player", text: t.slice(1, -1).trim() };
  if (t.startsWith("*") && t.endsWith("*") && t.length > 2) return { kind: "action", text: t.slice(1, -1).trim() };
  const named = NAMED.exec(t);
  if (named && isName(named[1])) return { kind: "speaker", who: named[1].trim(), text: named[2].trim() };
  return { kind: "speaker", text: t };
}

// Is there anything to play? Callers ask BEFORE routing, so a node with
// no words never flashes an empty screen on its way to the duel.
export const hasScene = (lines) => Array.isArray(lines) && lines.filter(Boolean).length > 0;

const storyScript = { NAMED, isName, readLine, hasScene };
export default storyScript;
